import React, { useMemo } from "react";
import { Helmet as ReactHelmet } from "react-helmet";

import { useStatContext } from "@/contexts/stat-context";
import { useInstance } from "@/hooks";
import FaviconService from "@/utils/favicon-service";

FaviconService.initFaviconService();

interface HelmetProps {
  children?: React.ReactNode;
  description?: string;
  imageUrl?: string;
  title?: string;
}

const Helmet: React.FC<HelmetProps> = ({
  children,
  description,
  imageUrl,
  title,
}) => {
  const instance = useInstance();
  const { unreadChatsCount, unreadAlertsCount } = useStatContext();
  const unreadCount = unreadAlertsCount + unreadChatsCount;

  const hasUnreadAlerts = React.useMemo(() => unreadCount > 0, [unreadCount]);

  const addCounter = (string: string) => {
    return hasUnreadAlerts ? `(${unreadCount}) ${string}` : string;
  };

  const updateFaviconBadge = () => {
    if (hasUnreadAlerts) {
      FaviconService.drawFaviconBadge();
    } else {
      FaviconService.clearFaviconBadge();
    }
  };

  const filteredTitle = useMemo(() => {
    if (title !== "Home") {
      return title;
    }
  }, [title]);

  const renderTitle = () => {
    if (!title) {
      return instance.title;
    }

    return `${filteredTitle} | ${instance.title}`;
  };

  const renderDescription = () => {
    return description || instance.short_description || instance.description;
  };

  const renderImageUrl = () => {
    return imageUrl || instance.thumbnail;
  };

  React.useEffect(() => {
    updateFaviconBadge();
  }, [unreadCount]);

  return (
    <ReactHelmet
      titleTemplate={addCounter(`%s | ${instance.title}`)}
      defaultTitle={addCounter(instance.title)}
      defer={false}
      title={filteredTitle}
    >
      <meta property="og:title" content={renderTitle()} />
      <meta name="twitter:title" content={renderTitle()} />
      <meta name="description" content={renderDescription()} />
      <meta property="og:description" content={renderDescription()} />
      <meta name="twitter:description" content={renderDescription()} />
      <meta property="og:image" content={renderImageUrl()} />
      <meta name="twitter:image" content={renderImageUrl()} />
      <meta property="og:site_name" content={instance.title} />
      <meta property="og:url" content={window.location.href} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />

      {children}
    </ReactHelmet>
  );
};

export default Helmet;
